import { externalNetworkClient } from '@terra/shared-rest-clients-external-network';
import { internalNetworkClient } from '@terra/shared-rest-clients-internal-network';
import { companyClient } from '@terra/shared-rest-clients-company';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type {
  AiApplicationsContract,
  AiComponentsListQueryInput,
} from '@terra/shared-contracts-ai-applications';
import { aiApplicationsClient } from '@terra/shared-rest-clients-ai-applications';
import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withOrchestratorClient,
} from './api-core';
import { getAuthHeaders } from '../../utils/authHeaders';

const SURFACE_CATALOG_PAGE_SIZE = 500;

export const catalogApi = {
  getAllCompanies: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await companyClient.getAllCompanies({
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return {
          status: response.status,
          body: response.body,
          headers: response.headers,
        } as const;
      }

      return response;
    } catch (error) {
      console.error('Failed to get companies:', error);

      throw error;
    }
  },

  getAllApplications: async () => {
    const headers = await createApiHeaders();

    return applicationsClient.getAllApplications({
      ...withOrchestratorClient(headers),
    });
  },

  getAiApplications: async () => {
    const headers = await createApiHeaders();

    return aiApplicationsClient.list({
      query: {},
      ...withOrchestratorClient(headers),
    });
  },

  getAiApplication: (aiApplicationId: string) =>
    tenantAwareApiWrapper<typeof AiApplicationsContract.getById>({
      endpointFn: (headers) =>
        aiApplicationsClient.getById({
          params: { aiApplicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getAiApplicationStats: (aiApplicationId: string) =>
    tenantAwareApiWrapper<typeof AiApplicationsContract.getStats>({
      endpointFn: (headers) =>
        aiApplicationsClient.getStats({
          params: { aiApplicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getAiApplicationComponents: (
    aiApplicationId: string,
    query?: AiComponentsListQueryInput,
  ) =>
    tenantAwareApiWrapper<typeof AiApplicationsContract.listComponents>({
      endpointFn: (headers) =>
        aiApplicationsClient.listComponents({
          params: { aiApplicationId },
          ...(query ? { query } : {}),
          ...withOrchestratorClient(headers),
        }),
    }),

  getExternalNetworks: async () => {
    const headers = await createApiHeaders();
    const clientOptions = withOrchestratorClient(headers);
    const surfaces = [];
    let skip = 0;
    let total = 0;

    do {
      const response = await externalNetworkClient.list({
        query: { limit: SURFACE_CATALOG_PAGE_SIZE, skip },
        ...clientOptions,
      });

      if (response.status !== 200) {
        return response;
      }

      const pageData = response.body.data;

      surfaces.push(...pageData);
      total = response.body.total;
      if (pageData.length === 0) {
        break;
      }

      skip += pageData.length;
    } while (skip < total && surfaces.length < total);

    return {
      status: 200 as const,
      body: {
        data: surfaces,
        total: surfaces.length,
      },
      headers: new Headers(),
    };
  },

  getInternalNetworks: async () => {
    const headers = await createApiHeaders();
    const clientOptions = withOrchestratorClient(headers);
    const networks = [];
    let skip = 0;
    let total = 0;

    do {
      const response = await internalNetworkClient.list({
        query: { limit: SURFACE_CATALOG_PAGE_SIZE, skip },
        ...clientOptions,
      });

      if (response.status !== 200) {
        return response;
      }

      const pageData = response.body.data;

      networks.push(...pageData);
      total = response.body.total;
      if (pageData.length === 0) {
        break;
      }

      skip += pageData.length;
    } while (skip < total && networks.length < total);

    return {
      status: 200 as const,
      body: {
        data: networks,
        total: networks.length,
      },
      headers: new Headers(),
    };
  },
};
